import { useState } from 'react'
import type { ShopifyOrderSummary } from '@fitting-room/contracts'
import { List, Pencil } from 'lucide-react'
import { useShopifyRecentOrders } from '../../../lib/adminQueries'
import { ApiRequestError } from '../../../lib/api'
import { boutiqueDateTime } from '../../../lib/boutique'
import { formatCents } from '../../../lib/orders'
import { financialStatusLabel } from '../../../lib/payments'
import { useStaffToken } from '../../../lib/staffToken'

/**
 * How far back the picker looks, and how many it reads: as far as Shopify's
 * `read_orders` reaches (60 days), the newest hundred of them. A payment is
 * usually recorded the day it was rung up; a deposit found missing at pickup
 * is the one weeks old.
 */
const PICKER_HOURS = 1440
const PICKER_LIMIT = 100

/** Where an order was rung up, as the counter says it. */
function channelWord(order: ShopifyOrderSummary): string {
  return order.channel === 'pos' ? 'In store' : order.channel === 'web' ? 'Online' : (order.sourceName ?? 'Other')
}

/** One Shopify order in a line: the number, the money, when, who, where, and its state when it isn't simply paid. */
export function shopifyOrderLabel(order: ShopifyOrderSummary): string {
  const status = financialStatusLabel(order.financialStatus)
  return [order.name, formatCents(order.totalMinor), boutiqueDateTime(order.createdAt), order.customerName, channelWord(order), status]
    .filter(Boolean)
    .join(' · ')
}

/**
 * The Shopify order a payment was rung up under: a dropdown of the
 * boutique's Shopify orders that no payment has yet, newest first, each with
 * Shopify's own date, amount, customer and register — and, beside it, a
 * pencil to type the number instead, for one older than the list or rung up
 * where the console can't see.
 *
 * Picking one hands the whole Shopify order to `onPick`, which fills what
 * it can (the amount, the day); typing hands the text to `onType` and lets
 * go of any order picked. Without Shopify connected, or without its
 * `read_orders` permission, it is the typed field alone, saying why.
 */
export function ShopifyOrderField({
  picked,
  typed,
  onPick,
  onType,
  note,
  startTyping = false,
}: {
  readonly picked: ShopifyOrderSummary | null
  readonly typed: string
  readonly onPick: (order: ShopifyOrderSummary | null) => void
  readonly onType: (text: string) => void
  /** A word about the number and the method, when they disagree. */
  readonly note?: string | undefined
  /** Start on the typed field: there is a number already, to correct. */
  readonly startTyping?: boolean
}) {
  const { token } = useStaffToken()
  const [typing, setTyping] = useState(startTyping)
  const lookup = useShopifyRecentOrders(token, { hours: PICKER_HOURS, limit: PICKER_LIMIT }, !typing)
  const refusal = lookup.error instanceof ApiRequestError ? lookup.error : null
  // Not connected, or not allowed to read orders: there is no list to pick from.
  const noList = refusal !== null && (refusal.status === 409 || refusal.status === 403)
  const manual = typing || noList

  const open = (lookup.data?.orders ?? [])
    .filter((order) => !order.alreadyRecordedOn || order.id === picked?.id)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))

  const choose = (id: string) => {
    if (!id) return onPick(null)
    const order = open.find((o) => o.id === id)
    if (order) onPick(order)
  }

  return (
    <div className="mb-3 flex flex-col gap-1">
      <label className="st-eyebrow" htmlFor={manual ? 'shopify-order-typed' : 'shopify-order-pick'}>
        Shopify order
      </label>
      <div className="flex items-center gap-1.5">
        {manual ? (
          <input
            id="shopify-order-typed"
            className="st-input tabular-nums"
            maxLength={33}
            autoComplete="off"
            placeholder="1147"
            value={typed}
            onChange={(event) => onType(event.target.value)}
          />
        ) : (
          <select
            id="shopify-order-pick"
            className="st-select min-w-0 flex-1"
            value={picked?.id ?? ''}
            disabled={lookup.isPending}
            onChange={(event) => choose(event.target.value)}
            data-testid="shopify-order-pick"
          >
            <option value="">
              {lookup.isPending ? 'Loading Shopify orders…' : open.length === 0 ? 'No Shopify orders waiting for a payment' : 'Not rung up in Shopify'}
            </option>
            {open.map((order) => (
              <option key={order.id} value={order.id} data-shopify-order={order.name}>
                {shopifyOrderLabel(order)}
              </option>
            ))}
          </select>
        )}
        {noList ? null : manual ? (
          <button
            type="button"
            className="st-btn-ghost shrink-0 px-2"
            aria-label="Choose from Shopify instead"
            title="Choose from Shopify"
            onClick={() => setTyping(false)}
          >
            <List size={14} aria-hidden />
          </button>
        ) : (
          <button
            type="button"
            className="st-btn-ghost shrink-0 px-2"
            aria-label="Type the number instead"
            title="Type the number"
            // What was picked shows as its number, and stays picked until it is typed over.
            onClick={() => setTyping(true)}
          >
            <Pencil size={14} aria-hidden />
          </button>
        )}
      </div>
      <span className="st-meta text-xs">
        {note ??
          (noList
            ? `${refusal?.message.replace(/ Type the payment in instead\.$/, '') ?? ''} Type the number on the Shopify receipt, like 1147.`.trim()
            : manual
              ? 'The number on the Shopify receipt, like 1147 — cash rung up in Shopify included.'
              : lookup.isError
                ? 'Couldn’t reach Shopify just now. Press the pencil to type the number.'
                : 'Shopify orders from the last 60 days that no payment has yet. Not there? Press the pencil to type the number.')}
      </span>
    </div>
  )
}
